import React from 'react';
import { useNavigate } from 'react-router-dom';

const ExpertiseSection: React.FC = () => {
  const handleConsultationClick = () => {
    window.REACT_APP_NAVIGATE('/portal');
  };

  return (
    <section id="expertise-section" className="py-16 sm:py-20 bg-gray-50">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 lg:mb-16">
          <h2 className="text-2xl sm:text-3xl lg:text-4xl font-light text-gray-900 mb-3 sm:mb-4">
            Professional Services for Campaign Success
          </h2>
          <p className="text-sm sm:text-base lg:text-lg text-gray-600 max-w-3xl mx-auto">
            We provide customized OOH advertising solutions that achieve maximum ROI by combining data-driven strategies with local expertise.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-8 sm:mb-12 lg:mb-16">
          <div className="text-center p-3 sm:p-4 lg:p-6 rounded-xl bg-gradient-to-br from-blue-50 to-indigo-50 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4">
              <i className="ri-bar-chart-line text-white text-lg sm:text-xl lg:text-2xl"></i>
            </div>
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 lg:mb-3">Campaign Performance Analysis</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              Maximize campaign effectiveness through real-time monitoring and detailed performance analysis, providing continuous optimization.
            </p>
          </div>

          <div className="text-center p-3 sm:p-4 lg:p-6 rounded-xl bg-gradient-to-br from-green-50 to-emerald-50 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4">
              <i className="ri-map-pin-line text-white text-lg sm:text-xl lg:text-2xl"></i>
            </div>
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 lg:mb-3">Strategic Location Selection</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              Select optimal advertising locations that guarantee maximum exposure to target audiences based on big data and demographic analysis.
            </p>
          </div>

          <div className="text-center p-3 sm:p-4 lg:p-6 rounded-xl bg-gradient-to-br from-purple-50 to-violet-50 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4">
              <i className="ri-palette-line text-white text-lg sm:text-xl lg:text-2xl"></i>
            </div>
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 lg:mb-3">Creative Optimization</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              Create localized creatives that reflect regional cultural characteristics and consumer preferences, conducting A/B testing.
            </p>
          </div>

          <div className="text-center p-3 sm:p-4 lg:p-6 rounded-xl bg-gradient-to-br from-orange-50 to-red-50 hover:shadow-lg transition-shadow">
            <div className="w-10 h-10 sm:w-12 sm:h-12 lg:w-16 lg:h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-2 sm:mb-3 lg:mb-4">
              <i className="ri-shield-check-line text-white text-lg sm:text-xl lg:text-2xl"></i>
            </div>
            <h3 className="text-sm sm:text-base lg:text-lg font-semibold text-gray-900 mb-1 sm:mb-2 lg:mb-3">Regulatory Compliance Management</h3>
            <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
              Ensure risk-free and safe campaign execution by fully complying with advertising regulations and legal requirements in each country.
            </p>
          </div>
        </div>

        {/* Success Stories */}
        <div className="bg-gradient-to-r from-slate-50 to-blue-50 rounded-xl sm:rounded-2xl p-4 sm:p-6 lg:p-8 mb-6 sm:mb-8 lg:mb-12">
          <h3 className="text-lg sm:text-xl lg:text-2xl font-light text-center text-gray-900 mb-4 sm:mb-6 lg:mb-8">Verified Success Stories</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6 lg:gap-8">
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-blue-600 mb-1 sm:mb-2">+340%</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-2">Brand Awareness Increase</div>
              <div className="text-xs text-gray-600">Dubai, UAE | Global Luxury Brand</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-green-600 mb-1 sm:mb-2">+250%</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-2">App Download Increase</div>
              <div className="text-xs text-gray-600">Tokyo, Japan | Tech Startup</div>
            </div>
            <div className="text-center">
              <div className="text-xl sm:text-2xl lg:text-3xl font-light text-purple-600 mb-1 sm:mb-2">+180%</div>
              <div className="text-xs sm:text-sm font-semibold text-gray-900 mb-1 sm:mb-2">Dealer Visit Increase</div>
              <div className="text-xs text-gray-600">Riyadh, Saudi Arabia | Automotive Brand</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center">
          <button 
            onClick={handleConsultationClick}
            className="bg-blue-600 text-white px-6 sm:px-8 py-3 sm:py-4 rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm sm:text-base lg:text-lg whitespace-nowrap cursor-pointer"
          >
            Schedule Free Consultation
          </button>
        </div>
      </div>
    </section>
  );
};

export default ExpertiseSection;
